import { IAnyType, IServerProps } from 'common/types/common';
import { buildAbsoluteUrl } from 'common/utils/url';
import { Layout } from 'layout';
import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const CTA = dynamic(() => import('common/components/Cta').then(mod => mod.CTA));
const Services = dynamic(() => import('modules/services/Services').then(mod => mod.Services));
const HeroServices = dynamic(() =>
  import('modules/services/HeroServices').then(mod => mod.HeroServices),
);

const ServicesPage = () => {
  const { t } = useTranslation('meta');

  const services = t('pageServices:services', { returnObjects: true }) as IAnyType[];
  const servicesSingle = [
    {
      seo: {
        title: t('serviceSingle.mobile.title'),
        description: t('serviceSingle.mobile.description'),
      },
      image: { src: t('serviceSingle.mobile.image') },
      slug: t('serviceSingle.mobile.slug'),
    },
    {
      seo: {
        description: t('serviceSingle.website.description'),
        title: t('serviceSingle.website.title'),
      },
      image: { src: t('serviceSingle.website.image') },
      slug: t('serviceSingle.website.slug'),
    },
    {
      seo: {
        description: t('serviceSingle.webshop.description'),
        title: t('serviceSingle.webshop.title'),
      },
      image: { src: t('serviceSingle.webshop.image') },
      slug: t('serviceSingle.webshop.slug'),
    },
    {
      seo: {
        description: t('serviceSingle.custom.description'),
        title: t('serviceSingle.custom.title'),
      },
      image: { src: t('serviceSingle.custom.image') },
      slug: t('serviceSingle.custom.slug'),
    },
  ];
  const schemaMarkup = [...services, ...servicesSingle].map((service: IAnyType) => {
    const base = t('common:company.url');
    const url = service.slug ? buildAbsoluteUrl(base, service.slug) : '';
    return {
      '@type': 'Service',
      name: service.seo.title,
      image: service.image?.src || '',
      description: service.seo.description,
      brand: {
        '@type': 'Brand',
        name: 'Nordit d.o.o.',
        logo: {
          '@type': 'ImageObject',
          url: 'https://firebasestorage.googleapis.com/v0/b/nordit-83a0b.appspot.com/o/logo%2FNordit%20logo%20-%20primary%20white%2FNordit%20logo%20-%20primary%20white%402x.png?alt=media&token=d979ae78-414f-4fd1-bfb5-dc31ad8ce3cf',
        },
      },
      url,
    };
  });
  const seo = {
    alternate: t('services.alternate', { returnObjects: true }),
    description: t('services.description'),
    image: t('services.image'),
    schemaMarkup,
    slug: t('services.slug'),
    title: t('services.title'),
  };

  return (
    <Layout dark seo={seo}>
      <HeroServices />
      <Services />
      <CTA dark />
    </Layout>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
): Promise<IServerProps<IServicesPageProps>> => {
  const locale = await serverSideTranslations(context.locale || 'en', [
    'common',
    'meta',
    'pageServices',
  ]);

  return {
    props: {
      data: { referer: context.req.headers.referer || null },
      ...locale,
    },
  };
};

interface IServicesPageProps {
  data: { referer?: string | null } | Record<string, never>;
}

export default ServicesPage;
